/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file cashoutSlice.js
 * @description Redux slice for managing cashout related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import originalAxios from "axios";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";
import { getAuthAccountId } from "../reselect/auth-selector";
import { getCmsConfigBrandDetails } from "../reselect/cms-selector";

import { loadBalance, loadSingleWalletBalance } from "./balanceSlice";

/**
 * @function getInitialState
 * @description Creates the initial state object for the cashout slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (activeBetCount = 0, cashableBetCount = 0, activeBets = []) => ({
  activeBetCount,
  activeBets,
  cashableBetCount,
  cashoutBuildData: {},
  cashoutBuildDataLoading: false,
  cashoutConfirmed: false,
  cashoutFailed: false,
  cashoutProcessing: false,
  error: null,
  loading: false,
  loadingCount: false,
  cashoutRules: null,
  cashoutRuleLoading: false,
  cashoutRuleError: null,
  ruleCreationConfirmed: false,
  ruleCreationFailed: false,
  autoCashoutConfirmed: false,
  autoCashoutFailed: false,
});

/**
 * @function getCashoutRule
 * @description Async thunk for get cashout rule operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getCashoutRule = createAsyncThunk("cashout/getCashoutRule", async ({ betId }, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const accountId = getAuthAccountId(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(
      `/player/acc/${accountId}/cashout/${betId}/rule?originId=${originId}&lineId=${lineId}`,
    );

    return {
      cashoutRule: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to get cashout rule",
      name: "Cashout Rule Retrieval Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @function createCashoutRule
 * @description Async thunk for create cashout rule operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const createCashoutRule = createAsyncThunk(
  "cashout/createCashoutRule",
  async ({ betId, target, percentage, stake }, thunkAPI) => {
    try {
      const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
      const accountId = getAuthAccountId(thunkAPI.getState());

      const axios = createAxiosInstance(thunkAPI.dispatch, {
        authToken,
        language,
      });

      const result = await axios.post(
        `/player/acc/${accountId}/cashout/${betId}/rule?originId=${originId}&lineId=${lineId}`,
        {
          target,
          percentage,
          stake,
        },
      );

      return {
        cashoutRule: result.data,
      };
    } catch (err) {
      const customError = {
        message: err.response?.headers["x-information"] || "Unable to create cashout rule",
        name: "Cashout Rule Creation Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  },
);

/**
 * @function cancelCashoutRule
 * @description Async thunk for cancel cashout rule operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const cancelCashoutRule = createAsyncThunk("cashout/cancelCashoutRule", async ({ betId }, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const accountId = getAuthAccountId(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.put(
      `/player/acc/${accountId}/cashout/${betId}/rule/cancel?originId=${originId}&lineId=${lineId}`,
    );

    return {
      betId,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to cancel cashout rule",
      name: "Cashout Rule Cancellation Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

let allCountCancelToken = null;

/**
 * @function getActiveBetCount
 * @description Async thunk for get active bet count operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getActiveBetCount = createAsyncThunk("cashout/activeBetCount", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const accountId = getAuthAccountId(thunkAPI.getState());

    if (allCountCancelToken) {
      allCountCancelToken.cancel("Operation canceled due to newer incoming request.");
    }

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    // Save the cancel token for the current request
    allCountCancelToken = originalAxios.CancelToken.source();

    const result = await axios.get(`/player/acc/${accountId}/cashout/allCount?originId=${originId}&lineId=${lineId}`, {
      cancelToken: allCountCancelToken.token,
    }); // Pass the cancel token to the current request;

    return {
      activeBetCount: result.data.activeBetCount,
      cashableBetCount: result.data.cashableBetCount,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain the active bet count", // serializable (err.response.data)
      name: "Active Bet Count Retrieval Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @function getActiveBetDetail
 * @description Async thunk for get active bet detail operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getActiveBetDetail = createAsyncThunk("cashout/activeBetDetail", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const accountId = getAuthAccountId(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });
    const result = await axios.get(
      `/player/acc/${accountId}/cashout/allDetails?originId=${originId}&lineId=${lineId}${
        data?.compactSpread ? "&compactSpread=true" : ""
      }`,
    );

    return {
      activeBets: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain the active bet count", // serializable (err.response.data)
      name: "Active Bet Count Retrieval Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @function putActiveBetDetailBuild
 * @description Async thunk for put active bet detail build operations | Cashout Build
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const putActiveBetDetailBuild = createAsyncThunk(
  "cashout/build",
  async ({ betId, percentage = 1 }, thunkAPI) => {
    try {
      const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
      const accountId = getAuthAccountId(thunkAPI.getState());

      const axios = createAxiosInstance(thunkAPI.dispatch, {
        authToken,
        language,
      });

      const response = await axios.put(`/player/acc/${accountId}/cashout/build?originId=${originId}&lineId=${lineId}`, {
        betId,
        percentage,
        originId: Number(originId),
        lineId,
      });

      return { activeBets: response.data };
    } catch (err) {
      const customError = {
        message: err.response?.headers["x-information"] || "Unable to build the bet", // serializable (err.response.data)
        name: "Bet Build Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  },
);

/**
 * @function getFreshQuotation
 * @description Async thunk for get fresh quotation operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getFreshQuotation = createAsyncThunk("cashout/quote", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const accountId = getAuthAccountId(thunkAPI.getState());

    const body = { betBucketId: data["betBucketId"], quote: data["quote"] };

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });
    const result = await axios.put(
      `/player/acc/${accountId}/cashout/details?originId=${originId}&lineId=${lineId}&compactSpread=${
        data.compactSpread || false
      }`,
      body,
    );

    return {
      activeBet: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain the active bet count", // serializable (err.response.data)
      name: "Active Bet Count Retrieval Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @function cashout
 * @description Async thunk for cashout operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const cashout = createAsyncThunk(
  "cashout/cashout",
  async ({ betBucketId, percentage, quote, stake }, thunkAPI) => {
    try {
      const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
      const accountId = getAuthAccountId(thunkAPI.getState());
      const cmsConfigBrandDetails = getCmsConfigBrandDetails(thunkAPI.getState());

      const axios = createAxiosInstance(thunkAPI.dispatch, {
        authToken,
        language,
      });
      const result = await axios.post(`/player/acc/${accountId}/cashout?originId=${originId}&lineId=${lineId}`, {
        betBucketId,
        lineId,
        originId,
        percentage,
        quote,
        stake,
      });

      if (result.data?.request?.statusId !== 1) {
        throw new Error("Cashout was rejected due to bet changes");
      }

      // thunkAPI.dispatch(getActiveBetDetail()); // refresh cleanly after a cashout... No longer done here, we do it after a cashout confirmation (user clicks OK)
      thunkAPI.dispatch(getActiveBetCount()); // refresh cleanly after a cashout...

      // Get me the latest balance...
      if (cmsConfigBrandDetails?.data?.singleWalletMode) {
        setTimeout(() => {
          thunkAPI.dispatch(loadSingleWalletBalance());
        }, 3000);
      } else {
        thunkAPI.dispatch(loadBalance());
      }

      return {
        activeBet: result.data,
      };
    } catch (err) {
      const customError = {
        message: err.response?.headers["x-information"] || "Unable to cashout", // serializable (err.response.data)
        name: "Cashout Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  },
);

/**
 * @constant cashoutSlice
 * @description Redux slice for cashout management
 */
const cashoutSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(cancelCashoutRule.pending, (state) => {
        state.cashoutRuleLoading = true;
      })
      .addCase(cancelCashoutRule.rejected, (state, action) => {
        state.cashoutRuleLoading = false;
        state.cashoutRuleError = action.error.message;
      })
      .addCase(cancelCashoutRule.fulfilled, (state, action) => {
        const { betId } = action.payload;
        state.cashoutRuleLoading = false;
        state.cashoutRuleError = null;
        state.cashoutRule[betId] = null;
      })
      .addCase(getCashoutRule.rejected, (state, action) => {
        state.cashoutRuleError = action.error.message;
      })
      .addCase(getCashoutRule.fulfilled, (state, action) => {
        const rule = action.payload.cashoutRule;

        if (rule?.betId) {
          state.cashoutRule = {
            ...state.cashoutRule,
            [rule.betId]: rule,
          };
          state.cashoutRuleError = null;
        }
        state.cashoutRuleError = null;
      })
      .addCase(createCashoutRule.pending, (state) => {
        state.cashoutRuleLoading = true;
      })
      .addCase(createCashoutRule.rejected, (state, action) => {
        state.cashoutRuleLoading = false;
        state.cashoutRuleError = action.error.message;
        state.ruleCreationFailed = true;
      })
      .addCase(createCashoutRule.fulfilled, (state, action) => {
        state.cashoutRules = action.payload.cashoutRule;
        state.cashoutRuleLoading = false;
        state.cashoutRuleError = null;
        state.ruleCreationConfirmed = true;
      })
      .addCase(getActiveBetCount.pending, (state) => {
        state.error = null;
        state.loadingCount = true;
      })
      .addCase(getActiveBetCount.rejected, (state, action) => {
        state.error = action.error.message;
        state.loadingCount = false;
      })
      .addCase(getActiveBetCount.fulfilled, (state, action) => {
        state.activeBetCount = action.payload.activeBetCount;
        state.cashableBetCount = action.payload.cashableBetCount;
        state.error = null;
        state.loadingCount = false;
      })
      .addCase(getActiveBetDetail.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getActiveBetDetail.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getActiveBetDetail.fulfilled, (state, action) => {
        state.activeBets = action.payload.activeBets;
        state.error = null;
        state.loading = false;
      })
      .addCase(putActiveBetDetailBuild.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.cashoutBuildDataLoading = true;
      })
      .addCase(putActiveBetDetailBuild.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
        state.cashoutBuildDataLoading = false;
      })
      .addCase(putActiveBetDetailBuild.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        const activeBet = action.payload.activeBets;
        state.cashoutBuildData[activeBet.betId] = activeBet;
        state.cashoutBuildDataLoading = false;
      })
      .addCase(getFreshQuotation.pending, (state) => {
        state.error = null;
      })
      .addCase(getFreshQuotation.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(getFreshQuotation.fulfilled, (state, action) => {
        state.error = null;
        if (!action.payload.activeBet) {
          return;
        }
        const { betBucketId } = action.payload.activeBet;
        const betIndex = state.activeBets.findIndex((activeBet) => activeBet.betBucketId === betBucketId);
        if (betIndex === -1) {
          return;
        }
        state.activeBets[betIndex] = action.payload.activeBet;
      })
      .addCase(cashout.pending, (state) => {
        state.cashoutConfirmed = false;
        state.cashoutFailed = false;
        state.cashoutProcessing = true;
        state.error = null;
      })
      .addCase(cashout.rejected, (state, action) => {
        state.cashoutProcessing = false;
        state.cashoutFailed = true;
        state.error = action.error.message;
      })
      .addCase(cashout.fulfilled, (state) => {
        state.cashoutProcessing = false;
        state.cashoutConfirmed = true;
        state.error = null;
      });
  },
  initialState: getInitialState(),
  name: "cashout",
  reducers: {
    clearCashoutState(state) {
      state.cashoutFailed = false;
      state.cashoutConfirmed = false;
    },
    clearCashoutRuleState(state) {
      state.ruleCreationConfirmed = false;
      state.ruleCreationFailed = false;
      state.autoCashoutConfirmed = false;
      state.autoCashoutFailed = false;
    },
    autoCashoutConfirmed(state) {
      state.autoCashoutConfirmed = true;
    },
    autoCashoutFailed(state, action) {
      state.autoCashoutFailed = {
        failed: true,
        status: action.payload.status,
      };
    },
    removeRule(state, action) {
      const { betId } = action.payload;
      state.cashoutRule[betId] = null;
    },
  },
});
const { actions, reducer } = cashoutSlice;
export const { clearCashoutState, clearCashoutRuleState, autoCashoutConfirmed, autoCashoutFailed, removeRule } =
  actions;
export default reducer;
